/**
 * Calculate month difference for calendar navigation
 * @param currentFirstMonth The current first visible month
 * @param targetDate The date to navigate to
 * @param calendar The calendar view type ('yearly' or 'monthly')
 * @returns The number of months to navigate
 */
export function calculateMonthDifference(
  currentFirstMonth: Date,
  targetDate: Date,
  calendar: string,
): number {
  if (calendar === 'yearly') {
    return calculateYearlyViewDifference(currentFirstMonth, targetDate);
  } else {
    return calculateMonthlyViewDifference(currentFirstMonth, targetDate);
  }
}

/**
 * For yearly view, maintain the current month as the first record
 */
function calculateYearlyViewDifference(
  currentFirstMonth: Date,
  targetDate: Date,
): number {
  const today = new Date();
  const currentMonthOfYear = today.getMonth(); // The month that should always be first

  // Calculate which year cycle the target date falls into
  const targetYear = targetDate.getFullYear();
  const targetMonth = targetDate.getMonth();

  // Determine the start year of the cycle that contains the target date
  let cycleStartYear: number;
  if (targetMonth >= currentMonthOfYear) {
    // Target is in or after current month, so it's in the cycle starting this year
    cycleStartYear = targetYear;
  } else {
    // Target is before current month, so it's in the cycle starting previous year
    cycleStartYear = targetYear - 1;
  }

  // Calculate the target first month (current month of the cycle start year)
  const targetFirstYear = cycleStartYear;
  const targetFirstMonth = currentMonthOfYear;

  // Calculate month difference from current first month to target first month
  const currentYear = currentFirstMonth.getFullYear();
  const currentMonth = currentFirstMonth.getMonth();

  return (
    (targetFirstYear - currentYear) * 12 + (targetFirstMonth - currentMonth)
  );
}

/**
 * For monthly view, navigate to make the target month the first month
 */
function calculateMonthlyViewDifference(
  currentFirstMonth: Date,
  targetDate: Date,
): number {
  const currentYear = currentFirstMonth.getFullYear();
  const currentMonth = currentFirstMonth.getMonth();
  const targetYear = targetDate.getFullYear();
  const targetMonth = targetDate.getMonth();

  return (targetYear - currentYear) * 12 + (targetMonth - currentMonth);
}

/**
 * Check if a month/year is visible in the current view
 */
export function isMonthVisible(
  targetMonth: number,
  targetYear: number,
  visibleMonths: { month: Date | null }[],
): boolean {
  for (const view of visibleMonths) {
    if (view && view.month) {
      const viewMonth = view.month.getMonth();
      const viewYear = view.month.getFullYear();

      if (targetMonth === viewMonth && targetYear === viewYear) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Checks if the selected date has actually changed from the previous selection
 */
export function hasDateChanged(
  previousDate: Date | undefined,
  currentDate: Date | undefined,
  dateEqualsFunc: (date1: Date, date2: Date) => boolean,
): boolean {
  return (
    !previousDate || !currentDate || !dateEqualsFunc(previousDate, currentDate)
  );
}

/**
 * Updates the selected date across all month views
 * - Sets the date on the matching month view
 * - Clears selection on other month views
 * - Returns the actual day object from the matching month view (with absence data)
 */
export function updateMonthViewSelections<
  T extends {
    month: Date | null;
    setDate: (date: Date | null) => void;
    days: any[];
  },
>(
  selectedDate: Date | undefined,
  views: T[],
  isSameMonthAndYearFunc: (date1: Date, date2: Date) => boolean,
  dateEqualsFunc: (date1: Date, date2: Date) => boolean,
): any | undefined {
  if (!selectedDate) {
    return undefined;
  }

  let dayWithData: any | undefined = undefined;

  for (const monthView of views) {
    if (monthView && monthView.month) {
      if (isSameMonthAndYearFunc(selectedDate, monthView.month)) {
        monthView.setDate(selectedDate);
        // Find the actual day object from this month view's days array
        dayWithData = monthView.days.find((d) =>
          dateEqualsFunc(d.date, selectedDate),
        );
      } else {
        // Clear selection from other months
        monthView.setDate(null);
      }
    }
  }

  return dayWithData;
}

/**
 * Determines if navigation to a different month is needed for the selected date
 */
export function shouldNavigateToDate(
  selectedDate: Date,
  views: { month: Date | null }[],
): boolean {
  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();

  // Check if the selected date's month is currently visible
  return !isMonthVisible(selectedMonth, selectedYear, views);
}
